import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Crown, Check, Star, Sparkles, Users, Calendar, Tag, 
  Mail, ArrowRight, Loader2, ChevronDown, Heart
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import SquarePaymentForm from '../components/SquarePaymentForm';

const TIERS = {
  free: {
    name: "Basic Member",
    price: 0,
    period: "",
    icon: Users,
    color: "slate",
    features: [
      "Newsletter subscription",
      "Event notifications",
      "Community access"
    ],
    highlighted: false
  },
  bronze: {
    name: "Bronze Member",
    price: 2.99,
    period: "/month",
    icon: Star,
    color: "amber",
    features: [
      "All Basic features",
      "Priority booking (24hr early access)",
      "5% member discount on events",
      "Volunteer eligibility"
    ],
    highlighted: false
  },
  silver: {
    name: "Silver Member",
    price: 5.99,
    period: "/month",
    icon: Sparkles,
    color: "slate",
    features: [
      "All Bronze features",
      "10% member discount on events",
      "Working group eligibility",
      "Exclusive member events"
    ],
    highlighted: true
  },
  gold: {
    name: "Gold Member",
    price: 9.99,
    period: "/month",
    icon: Crown,
    color: "yellow",
    features: [
      "All Silver features",
      "15% member discount on events",
      "Free guest passes (2/year)",
      "VIP event access"
    ],
    highlighted: false
  }
};

export default function MembershipPage() {
  const [selectedTier, setSelectedTier] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    city: '',
    region: '',
    country: 'United Kingdom',
    dance_experience: '',
    newsletter_opt_in: true,
    volunteer_interest: false,
    working_group_interest: false
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  const handleTierSelect = (tierId) => {
    setSelectedTier(tierId);
    setShowSignup(true);
    // Scroll to signup form
    setTimeout(() => {
      document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.password || formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!formData.first_name.trim()) {
      toast.error('Please enter your first name');
      return false;
    }
    if (!formData.last_name.trim()) {
      toast.error('Please enter your last name');
      return false;
    }
    return true;
  };

  // Handle free tier registration
  const handleFreeRegistration = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/membership/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          password: formData.password,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          phone: formData.phone || null,
          city: formData.city || null,
          region: formData.region || null,
          country: formData.country,
          dance_experience: formData.dance_experience || null,
          newsletter_opt_in: formData.newsletter_opt_in,
          volunteer_interest: formData.volunteer_interest,
          working_group_interest: formData.working_group_interest
        })
      });

      if (res.ok) {
        const data = await res.json();
        setRegistrationData(data);
        setRegistrationComplete(true);
        toast.success('Welcome! Your free membership is now active.');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle paid tier payment success
  const handlePaymentSuccess = (data) => {
    setRegistrationData(data);
    setRegistrationComplete(true);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setLoading(false);
  };

  // Build payment body for paid tiers
  const getPaymentBody = () => {
    if (!validateForm()) return null;
    
    return {
      email: formData.email.trim(),
      password: formData.password,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      phone: formData.phone || null,
      city: formData.city || null,
      region: formData.region || null,
      country: formData.country,
      dance_experience: formData.dance_experience || null,
      newsletter_opt_in: formData.newsletter_opt_in,
      volunteer_interest: formData.volunteer_interest,
      working_group_interest: formData.working_group_interest,
      tier: selectedTier
    };
  };

  // Registration complete view
  if (registrationComplete) {
    const tier = TIERS[selectedTier];
    const isPaid = selectedTier !== 'free';
    
    return (
      <div className="min-h-screen bg-[#FAF9F6] py-12">
        <div className="max-w-2xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {isPaid ? 'Payment Successful!' : 'Welcome to Line Dance Central!'}
            </h1>
            <p className="text-slate-600 mb-6">
              {isPaid 
                ? "Thank you for joining! Your membership is pending admin approval. We'll email you when it's activated."
                : "Your free membership is now active. Welcome to the community!"}
            </p>
            
            <Card className="text-left mb-6">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <tier.icon className="h-5 w-5" />
                  {tier.name}
                </CardTitle>
                <CardDescription>
                  {isPaid ? 'Pending Approval' : 'Active'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <p className="text-sm text-slate-600">
                    <strong>Email:</strong> {registrationData?.email || formData.email}
                  </p>
                  <p className="text-sm text-slate-600">
                    <strong>Member ID:</strong> {registrationData?.member_id}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-3">
              <Button asChild className="w-full">
                <a href="/">
                  Explore Line Dance Central
                  <ArrowRight className="h-4 w-4 ml-2" />
                </a>
              </Button>
              {isPaid && (
                <p className="text-sm text-slate-500">
                  We&apos;ll send a confirmation email once your membership is approved (usually within 24 hours).
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 text-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              <Heart className="h-3 w-3 mr-1" /> Join Our Community
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              Become a Member
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              Join Line Dance Central and unlock exclusive benefits, discounts, and be part of our amazing community.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Membership Tiers */}
      <div className="max-w-6xl mx-auto px-4 py-12 -mt-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(TIERS).map(([tierId, tier], index) => {
            const Icon = tier.icon;
            const isSelected = selectedTier === tierId;
            
            return (
              <motion.div
                key={tierId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card 
                  className={`relative h-full transition-all duration-300 hover:shadow-lg cursor-pointer ${
                    tier.highlighted 
                      ? 'border-2 border-violet-500 shadow-lg' 
                      : isSelected 
                        ? 'border-2 border-violet-500' 
                        : 'border border-slate-200 hover:border-violet-300'
                  }`}
                  onClick={() => handleTierSelect(tierId)}
                  data-testid={`tier-${tierId}`}
                >
                  {tier.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-violet-600 text-white">Most Popular</Badge>
                    </div>
                  )}
                  
                  <CardHeader className="text-center pb-2">
                    <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
                      tierId === 'gold' ? 'bg-yellow-100' :
                      tierId === 'silver' ? 'bg-slate-100' :
                      tierId === 'bronze' ? 'bg-amber-100' :
                      'bg-slate-100'
                    }`}>
                      <Icon className={`h-6 w-6 ${
                        tierId === 'gold' ? 'text-yellow-600' :
                        tierId === 'silver' ? 'text-slate-600' :
                        tierId === 'bronze' ? 'text-amber-600' :
                        'text-slate-600'
                      }`} />
                    </div>
                    <CardTitle className="text-lg">{tier.name}</CardTitle>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-slate-900">
                        {tier.price === 0 ? 'Free' : formatCurrency(tier.price)}
                      </span>
                      {tier.period && (
                        <span className="text-slate-500 text-sm">{tier.period}</span>
                      )}
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-4">
                    <ul className="space-y-3">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  
                  <CardFooter>
                    <Button 
                      className={`w-full ${
                        tier.highlighted || isSelected
                          ? 'bg-violet-600 hover:bg-violet-700' 
                          : ''
                      }`}
                      variant={tier.highlighted || isSelected ? 'default' : 'outline'}
                      data-testid={`select-${tierId}`}
                    >
                      {isSelected ? 'Selected' : 'Choose Plan'}
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Signup Form */}
      {showSignup && selectedTier && (
        <div id="signup-form" className="max-w-2xl mx-auto px-4 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card>
              <CardHeader>
                {(() => {
                  const TierIcon = TIERS[selectedTier].icon;
                  return (
                    <CardTitle className="flex items-center gap-2">
                      <TierIcon className="h-5 w-5" />
                      Sign Up for {TIERS[selectedTier].name}
                    </CardTitle>
                  );
                })()}
                <CardDescription>
                  {selectedTier === 'free' 
                    ? 'Create your free account to get started'
                    : `${formatCurrency(TIERS[selectedTier].price)}/month - Your membership will be activated after admin approval`
                  }
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Account Info */}
                <div>
                  <h3 className="font-medium text-slate-900 mb-3">Account Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="you@example.com"
                        data-testid="signup-email"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="password">Password *</Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                          placeholder="Min 6 characters"
                          data-testid="signup-password"
                        />
                      </div>
                      <div>
                        <Label htmlFor="confirmPassword">Confirm Password *</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                          placeholder="Confirm password"
                          data-testid="signup-confirm-password"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Personal Info */}
                <div>
                  <h3 className="font-medium text-slate-900 mb-3">Personal Details</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input
                          id="first_name"
                          value={formData.first_name}
                          onChange={(e) => handleInputChange('first_name', e.target.value)}
                          placeholder="John"
                          data-testid="signup-first-name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input
                          id="last_name"
                          value={formData.last_name}
                          onChange={(e) => handleInputChange('last_name', e.target.value)}
                          placeholder="Smith"
                          data-testid="signup-last-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone (optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        placeholder="07123 456789"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city">City/Town</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          placeholder="London"
                        />
                      </div>
                      <div>
                        <Label htmlFor="region">County/Region</Label>
                        <Input
                          id="region"
                          value={formData.region}
                          onChange={(e) => handleInputChange('region', e.target.value)}
                          placeholder="Greater London"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="dance_experience">Dance Experience</Label>
                      <Select 
                        value={formData.dance_experience} 
                        onValueChange={(v) => handleInputChange('dance_experience', v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your experience level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                          <SelectItem value="instructor">Instructor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Preferences */}
                <div>
                  <h3 className="font-medium text-slate-900 mb-3">Preferences</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="newsletter"
                        checked={formData.newsletter_opt_in}
                        onCheckedChange={(checked) => handleInputChange('newsletter_opt_in', checked)}
                      />
                      <Label htmlFor="newsletter" className="text-sm font-normal cursor-pointer">
                        Subscribe to our newsletter for event updates and news
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="volunteer"
                        checked={formData.volunteer_interest}
                        onCheckedChange={(checked) => handleInputChange('volunteer_interest', checked)}
                      />
                      <Label htmlFor="volunteer" className="text-sm font-normal cursor-pointer">
                        I&apos;m interested in volunteering at events
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="working_group"
                        checked={formData.working_group_interest}
                        onCheckedChange={(checked) => handleInputChange('working_group_interest', checked)}
                      />
                      <Label htmlFor="working_group" className="text-sm font-normal cursor-pointer">
                        I&apos;m interested in joining a working group
                      </Label>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Payment or Submit */}
                {selectedTier === 'free' ? (
                  <Button 
                    className="w-full bg-violet-600 hover:bg-violet-700"
                    onClick={handleFreeRegistration}
                    disabled={loading}
                    data-testid="submit-free"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        Create Free Account
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                ) : (
                  <div>
                    <div className="bg-slate-50 p-4 rounded-lg mb-4">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-600">Monthly membership</span>
                        <span className="font-bold text-lg">{formatCurrency(TIERS[selectedTier].price)}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Your membership will be activated after admin approval (usually within 24 hours)
                      </p>
                    </div>
                    <SquarePaymentForm
                      amount={Math.round(TIERS[selectedTier].price * 100)}
                      currency="GBP"
                      customPaymentEndpoint={`${API_URL}/api/membership/register-paid`}
                      customPaymentBody={getPaymentBody()}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPaymentError={handlePaymentError}
                      disabled={loading || !formData.email || !formData.password || !formData.first_name || !formData.last_name}
                      buttonText={`Pay ${formatCurrency(TIERS[selectedTier].price)} & Join`}
                      title="Secure payment powered by Square"
                      customerName={`${formData.first_name} ${formData.last_name}`}
                      customerEmail={formData.email}
                    />
                  </div>
                )}

                <p className="text-xs text-slate-500 text-center">
                  By signing up, you agree to our terms of service and privacy policy.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Benefits Section */}
      <div className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-slate-900 mb-12">
            Why Join Line Dance Central?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Priority Event Access</h3>
              <p className="text-slate-600">
                Get early access to book popular events before they sell out
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Tag className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Exclusive Discounts</h3>
              <p className="text-slate-600">
                Save up to 15% on events, workshops, and merchandise
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-violet-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Community Connection</h3>
              <p className="text-slate-600">
                Connect with fellow dancers and be part of something special
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
